/**
 * Watchdog — fail-open timeout guards for reporter phases.
 *
 * A Playwright reporter runs *inside* the customer's `playwright test` process.
 * Playwright awaits `onBegin`/`onEnd`, so anything the reporter awaits that never
 * settles wedges the entire test run — before a single test executes, in the
 * `onBegin` case. Reporting is telemetry: it must never be load-bearing for the
 * customer's tests.
 *
 * Every await in the reporter that touches the network, auth, the filesystem, or
 * a child process goes through `withTimeout`, which guarantees three things:
 *
 *  1. **It always settles.** A pending operation is abandoned after `timeoutMs`.
 *  2. **It never rejects.** A rejection becomes `undefined` — callers cannot be
 *     made to crash the run by a telemetry failure.
 *  3. **It never holds the process open.** The timer is `unref()`d, so a pending
 *     watchdog cannot by itself keep Node's event loop alive at exit.
 *
 * On expiry a single line goes to stderr, so a stalled run is diagnosable
 * instead of silent — the failure mode that made the 2.11.1 hang so expensive.
 */

/** Sentinel distinguishing "timer won the race" from a legitimate `undefined`. */
const TIMED_OUT: unique symbol = Symbol('testrelic.timeout');

/** Cloud auth + run-init during `onBegin`. Blocks the first test, so keep it tight. */
export const CLOUD_INIT_TIMEOUT_MS = 30_000;

/** Cloud finalize + result upload during `onEnd`. Larger payloads, more headroom. */
export const CLOUD_FINALIZE_TIMEOUT_MS = 120_000;

/** Report finalization / artifact flush — local disk I/O, should be fast. */
export const REPORT_FINALIZE_TIMEOUT_MS = 60_000;

/** Cloud client teardown (timer cleanup + queue flush). */
export const CLOUD_DISPOSE_TIMEOUT_MS = 15_000;

/**
 * Run `operation` with a hard upper bound, failing open on timeout or rejection.
 *
 * Resolves with the operation's value, or `undefined` if it timed out or threw.
 * Never rejects. The abandoned promise keeps running (we cannot cancel an
 * arbitrary promise) but no longer blocks the caller — the point is that the
 * customer's test run proceeds regardless.
 *
 * @param operation - Factory invoked immediately; a throwing factory is caught too.
 * @param timeoutMs - Upper bound in milliseconds.
 * @param label     - Human-readable phase name used in the stderr warning.
 */
export async function withTimeout<T>(
  operation: () => Promise<T>,
  timeoutMs: number,
  label: string,
): Promise<T | undefined> {
  let timer: ReturnType<typeof setTimeout> | undefined;

  const timeout = new Promise<typeof TIMED_OUT>((resolve) => {
    timer = setTimeout(() => resolve(TIMED_OUT), timeoutMs);
    // Never let a pending watchdog be the reason the process cannot exit.
    if (typeof timer?.unref === 'function') timer.unref();
  });

  try {
    // Invoking inside the try means a factory that throws synchronously is
    // handled identically to one that returns a rejected promise.
    const result = await Promise.race([operation(), timeout]);

    if (result === TIMED_OUT) {
      process.stderr.write(
        `[testrelic] ${label} timed out after ${Math.round(timeoutMs / 1000)}s — ` +
          'continuing without it. Your tests are unaffected.\n',
      );
      return undefined;
    }
    return result as T;
  } catch (err) {
    process.stderr.write(
      `[testrelic] ${label} failed — continuing without it. Your tests are unaffected. ` +
        `(${err instanceof Error ? err.message : String(err)})\n`,
    );
    return undefined;
  } finally {
    // Clear eagerly so a completed phase leaves no timer behind at all.
    if (timer) clearTimeout(timer);
  }
}
